import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Icon } from "@/components/Icon";
import CheckoutSheet from "@/components/checkout/CheckoutSheet";
import LoadError from "@/components/ui/LoadError";
import EmptyState from "@/components/ui/EmptyState";
import PageContainer from "@/components/ui/PageContainer";
import { fmtMoney, fmtAmtNum } from "@/lib/money";
import { dailyRewardLabel, dailyRewardTerms, dailyRewardClaimMessage } from "@/lib/dailyReward";
import PayoutDomainSection from "@/components/payouts/PayoutDomainSection";
import { usePayoutDomainEnabled } from "@/components/payouts/usePayouts";

const TYPE_LABEL = {
  deposit: "Purchase", withdraw: "Prize Redemption", bet: "Bet placed",
  payout: "Winning payout", admin_credit: "Admin credit", bonus: "Bonus PCoins",
  tip_sent: "Tip sent", tip_received: "Tip received",
};
// Transaction status pills. sent/paid are positive progress tones (NEVER styled as rejected);
// failed is the only red terminal state.
const STATUS_STYLE = {
  approved: "bg-[#00D632]/15 text-[#00D632]",
  paid: "bg-[#00D632]/15 text-[#00D632]",
  sent: "bg-[#38bdf8]/15 text-[#38bdf8]",
  pending: "bg-surface-5 text-[#00D632]",
  rejected: "bg-[#EF4444]/15 text-[#EF4444]",
  failed: "bg-[#EF4444]/15 text-[#EF4444]",
};
// Human labels for the lifecycle states so pills don't read as raw backend strings.
const STATUS_LABEL = {
  approved: "Approved", paid: "Paid", sent: "Sent", pending: "Pending",
  rejected: "Rejected", failed: "Failed",
};
// Ordered redemption lifecycle for the per-row timeline. We render each stage that has
// either happened (timestamped) or is the current status; a failed request shows the
// failure terminal instead of sent/paid.
const WD_LIFECYCLE = [
  { key: "requested", label: "Requested" },
  { key: "approved", label: "Approved" },
  { key: "sent", label: "Sent" },
  { key: "paid", label: "Paid" },
];

// Mask a payout destination for display (activity feed + review summary) — keep enough to
// recognize it, hide the rest. Emails keep the domain; everything else keeps the last 4.
function maskDestination(detail) {
  const s = String(detail || "").trim();
  if (!s) return "";
  if (s.includes("@") && s.indexOf("@") > 0) {
    const [local, domain] = s.split("@");
    const head = local.slice(0, Math.min(2, local.length));
    return `${head}${"•".repeat(Math.max(1, local.length - head.length))}@${domain}`;
  }
  if (s.length <= 4) return s;
  return `${"•".repeat(Math.max(2, s.length - 4))}${s.slice(-4)}`;
}

// Map a transaction to the shared money model's currency word. PCash cash rows read exact
// cents ("$X"), bonus rows read "$X bonus", everything else reads "X PCoins" (UIA-05).
function txCurrency(t) {
  const c = t?.currency;
  if (c === "cash" || c === "bonus" || c === "coins") return c;
  // Legacy rows without an explicit currency: redemption/cash-flagged types are USD cents.
  if (["withdraw", "payout", "admin_credit"].includes(t?.type)) return "cash";
  return "coins";
}

const TXN_FILTERS = [
  { key: "all", label: "All", match: () => true },
  { key: "purchases", label: "Purchases", match: (t) => t.type === "deposit" },
  { key: "redemptions", label: "Redemptions", match: (t) => t.type === "withdraw" },
  { key: "bonuses", label: "Bonuses", match: (t) => ["bonus", "admin_credit", "tip_received"].includes(t.type) },
];

// §7 one-shot staggered entrance (reduced-motion guarded by the caller — falls back to static).
const SECTION_STAGGER = { show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } } };
const SECTION_ITEM = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
};

export default function Wallet() {
  const { user, refresh, streamerMode } = useAuth();
  const reduceMotion = useReducedMotion();
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [packages, setPackages] = useState([]);
  const [methods, setMethods] = useState({ stripe: true, razorpay: false });
  const [rzpKey, setRzpKey] = useState("");
  const [buying, setBuying] = useState("");
  const [firstBonus, setFirstBonus] = useState({ available: false, coinPct: 0, cashPct: 0 });
  const [customCfg, setCustomCfg] = useState({ min_usd: 5, max_usd: 1000, coins_per_usd: 1000, cash_per_usd: 1 });
  const [customUsd, setCustomUsd] = useState("");
  const [wdAmt, setWdAmt] = useState("");
  const [wdTag, setWdTag] = useState("");
  const [wdMethod, setWdMethod] = useState("PayPal");
  const [wdSaveDefault, setWdSaveDefault] = useState(false);
  const [pmEdit, setPmEdit] = useState(false);
  const [pmMethod, setPmMethod] = useState("PayPal");
  const [pmDetail, setPmDetail] = useState("");
  const [pmHolder, setPmHolder] = useState("");
  const [pmVerifying, setPmVerifying] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [chosenPromo, setChosenPromo] = useState("");
  const [wdDob, setWdDob] = useState("");
  const [kycId, setKycId] = useState(null);
  const [kycSelfie, setKycSelfie] = useState(null);
  const [kycBusy, setKycBusy] = useState(false);
  const [deepidvEnabled, setDeepidvEnabled] = useState(false);
  // Redemption limits come from the ONE authoritative payout policy (via /version), so this legacy
  // wallet UI shows/validates the SAME numbers the server enforces — a policy change updates it too.
  const [limits, setLimits] = useState({ min: 50, max: 1000, monthly_cap: 5000 });
  const [manualKyc, setManualKyc] = useState(false);
  const [busy, setBusy] = useState(false);
  const [depOpen, setDepOpen] = useState(false);
  const [wdOpen, setWdOpen] = useState(false);
  const [ref, setRef] = useState(null);
  const { enabled: payoutDomainEnabled } = usePayoutDomainEnabled();
  // Route the legacy "Redeem prizes" entry to the new flow when the domain owns redemptions.
  const onRedeem = () => {
    if (payoutDomainEnabled) {
      document.getElementById("payout-redemptions")?.scrollIntoView({ behavior: "smooth" });
    } else {
      setWdOpen(true);
    }
  };
  const [promos, setPromos] = useState([]);
  const [txnFilter, setTxnFilter] = useState("all");
  const [showAllTxns, setShowAllTxns] = useState(false);
  const [checkout, setCheckout] = useState(null); // { order, promoId } — branded in-app checkout sheet
  const [verif, setVerif] = useState(null); // /kyc/verification — dimension-aware identity/hold model (UIA-06)
  const [wdReview, setWdReview] = useState(false); // show the pre-submit redemption review summary (UIA-05)
  const [wdResult, setWdResult] = useState(null); // { amount, reference, method, detail } after a successful request (UIA-05)

  const load = async () => {
    try {
      const { data } = await api.get("/wallet");
      setData(data);
      setLoadErr(false);
    } catch (e) {
      // Surface a retryable error instead of spinning forever on a failed /wallet (UIA-02).
      setLoadErr(true);
    }
  };
  const loadRef = async () => {
    const { data } = await api.get("/referrals/me");
    setRef(data);
  };
  useEffect(() => { load(); loadRef(); api.get("/promotions").then(({ data }) => setPromos(data)).catch(() => {});
    api.get("/kyc/verification").then(({ data }) => setVerif(data)).catch(() => {});
    api.get("/kyc/status").then(({ data }) => setDeepidvEnabled(!!data.deepidv_enabled)).catch(() => {});
    api.get("/version").then(({ data }) => setLimits({
      min: Number(data.redemption_min ?? 50),
      max: Number(data.redemption_max_per_request ?? 1000),
      monthly_cap: Number(data.redemption_monthly_cap ?? 5000),
    })).catch(() => {});
    api.get("/payments/packages").then(({ data }) => { setPackages(data.packages); setMethods(data.methods); setRzpKey(data.razorpay_key_id || "");
      setFirstBonus({ available: !!data.first_purchase_available, coinPct: data.first_purchase_coin_pct || 0, cashPct: data.first_purchase_cash_pct || 0 });
      if (data.custom) setCustomCfg(data.custom); }).catch(() => {}); }, []);

  useEffect(() => {
    if (data?.saved_payout_method) setWdMethod(data.saved_payout_method);
    if (data?.saved_payout_detail) setWdTag(data.saved_payout_detail);
  }, [data?.saved_payout_method, data?.saved_payout_detail]);

  useEffect(() => {
    const dm = promos.filter((p) => p.promo_type === "deposit_match");
    if (dm.length && chosenPromo === "") setChosenPromo(dm[0].id);
  }, [promos]); // eslint-disable-line

  const startEditPm = () => {
    setPmMethod(data?.saved_payout_method || "PayPal");
    setPmDetail(data?.saved_payout_detail || "");
    setPmHolder("");
    setPmEdit(true);
  };
  const savePm = async () => {
    if (!pmDetail.trim()) { toast.error("Enter your payout details"); return; }
    try {
      await api.post("/wallet/payout-default", { payout_method: pmMethod, payout_detail: pmDetail.trim() });
      toast.success("Default redemption method saved");
      setPmEdit(false); await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const verifyPm = async () => {
    if (!pmDetail.trim()) { toast.error("Enter your payout destination first"); return; }
    if (!pmHolder.trim()) { toast.error("Enter the full name on the account"); return; }
    setPmVerifying(true);
    try {
      await api.post("/wallet/payout-destination/verify", {
        method: pmMethod, detail: pmDetail.trim(), account_holder_name: pmHolder.trim() });
      toast.success("Payout destination ownership verified");
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setPmVerifying(false); }
  };
  const clearPm = async () => {
    try {
      await api.delete("/wallet/payout-default");
      toast.success("Saved method cleared");
      setPmEdit(false); await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };


  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  // Open the branded in-app CheckoutSheet (Stripe Elements) instead of redirecting to
  // Stripe's hosted page. Builds the order line from the chosen pack / custom amount.
  const openCheckout = (order) => {
    setDepOpen(false);
    setCheckout({ order, promoId: chosenPromo || null });
  };

  const buyStripe = (lookup_key, custom_usd = null) => {
    const pkg = packages.find((p) => p.lookup_key === lookup_key);
    if (!pkg) { toast.error("Choose a coin pack"); return; }
    openCheckout({
      lookup_key, custom_usd: null,
      label: pkg.label, usd: pkg.usd, coins: pkg.coins, cash: pkg.cash,
      firstBonusAvailable: firstBonus.available,
      firstBonusCoins: firstBonus.available ? Math.round(pkg.coins * firstBonus.coinPct / 100) : 0,
      firstBonusCash: firstBonus.available ? +(pkg.cash * firstBonus.cashPct / 100).toFixed(2) : 0,
    });
  };

  const buyCustom = () => {
    const v = parseFloat(customUsd);
    if (isNaN(v) || v < customCfg.min_usd || v > customCfg.max_usd) {
      toast.error(`Enter an amount between $${customCfg.min_usd} and $${customCfg.max_usd}`); return;
    }
    const usd = Math.round(v * 100) / 100;
    const coins = Math.round(usd * customCfg.coins_per_usd);
    const cash = +(usd * customCfg.cash_per_usd).toFixed(2);
    openCheckout({
      lookup_key: "pnr_custom", custom_usd: usd,
      label: "Custom amount", usd, coins, cash,
      firstBonusAvailable: firstBonus.available,
      firstBonusCoins: firstBonus.available ? Math.round(coins * firstBonus.coinPct / 100) : 0,
      firstBonusCash: firstBonus.available ? +(cash * firstBonus.cashPct / 100).toFixed(2) : 0,
    });
  };

  const buyRazorpay = async (lookup_key) => {
    setBuying(lookup_key);
    const ok = await loadRazorpayScript();
    if (!ok) { toast.error("Could not load Razorpay"); setBuying(""); return; }
    try {
      const { data } = await api.post("/payments/razorpay/order", { lookup_key });
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: "PRYMEBET", description: data.label,
        prefill: { name: user?.username || "", email: user?.email || "" },
        theme: { color: "#00D632" },
        handler: async (res) => {
          try {
            const { data: v } = await api.post("/payments/razorpay/verify", {
              razorpay_order_id: res.razorpay_order_id, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature });
            const bonusMsg = v.first_purchase ? ` + first-buy bonus ${v.bonus_coins.toLocaleString()} PCoins & $${v.bonus_cash}` : "";
            const promoMsg = v.promo_bonus > 0 ? ` + ${v.promo_title || "match"} $${v.promo_bonus} bonus` : "";
            toast.success(`Purchased ${v.package}! +${v.coins.toLocaleString()} PCoins & $${v.cash} PCash${bonusMsg}${promoMsg}`);
            setDepOpen(false); await load(); await refresh();
          } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
        },
        modal: { ondismiss: () => setBuying("") },
      });
      rzp.open();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBuying(""); }
  };

  // Step 1: validate inputs and open the review summary (amount, masked destination, method)
  // before the request is actually submitted (UIA-05).
  const reviewWithdraw = () => {
    const amt = parseFloat(wdAmt);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (amt < limits.min) { toast.error(`Minimum redemption is $${limits.min.toLocaleString()}`); return; }
    if (amt > limits.max) { toast.error(`Maximum redemption is $${limits.max.toLocaleString()} per request`); return; }
    if (!wdTag.trim()) { toast.error("Enter your payout details"); return; }
    const needsDob = !(user && user.age_verified);
    if (needsDob) {
      if (!wdDob) { toast.error("Please confirm your date of birth (18+) to redeem"); return; }
      const age = Math.floor((Date.now() - new Date(wdDob).getTime()) / 31557600000);
      if (age < 18) { toast.error("You must be at least 18 to redeem"); return; }
    }
    setWdReview(true);
  };

  // Step 2: submit the reviewed request and surface the request id/reference on success.
  const submitWithdraw = async () => {
    const amt = parseFloat(wdAmt);
    const needsDob = !(user && user.age_verified);
    setBusy(true);
    try {
      const { data: res } = await api.post("/wallet/withdraw", { amount: amt, payout_detail: wdTag, payout_method: wdMethod, save_default: wdSaveDefault, dob: needsDob ? wdDob : undefined });
      toast.success("Redemption requested! An admin will process your prize shortly.");
      setWdResult({ amount: amt, reference: res?.payout_reference || res?.id || null, method: wdMethod, detail: wdTag.trim() });
      setWdReview(false);
      setWdAmt(""); setWdTag(""); setWdDob("");
      await load(); await refresh();
    } catch (e) {
      setWdReview(false);
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitKyc = async () => {
    if (!kycId || !kycSelfie) { toast.error("Please attach both your ID and a selfie"); return; }
    setKycBusy(true);
    try {
      const fd = new FormData();
      fd.append("id_document", kycId);
      fd.append("selfie", kycSelfie);
      await api.post("/kyc/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documents submitted! We'll review them shortly.");
      setKycId(null); setKycSelfie(null);
      await refresh();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setKycBusy(false); }
  };

  const startDeepidv = async () => {
    setKycBusy(true);
    try {
      const { data } = await api.post("/kyc/deepidv/start", {});
      if (data?.session_url) { window.location.href = data.session_url; return; }
      toast.error("Could not start identity verification. Please try again.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not start identity verification.");
    } finally { setKycBusy(false); }
  };

  const claimBonus = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/wallet/claim-bonus");
      toast.success(dailyRewardClaimMessage(d));
      await load(); await refresh();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!data) return (
    <PageContainer className="max-w-2xl">
      {loadErr ? (
        <LoadError onRetry={load} message="We couldn't load your wallet." testid="wallet-load-error" />
      ) : (
        /* Loading skeleton matching the wallet layout (§4.10) — balance cards + activity
           rows — rather than a bare spinner, so the shape doesn't jump on load. */
        <div data-testid="wallet-skeleton" aria-hidden="true" className="space-y-6">
          <div className="flex items-end justify-between gap-4">
            <span className="h-9 w-40 animate-pulse rounded-lg bg-surface-4" />
            <span className="h-10 w-28 animate-pulse rounded-full bg-surface-4" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex flex-col gap-3 rounded-2xl border border-surface-border bg-surface-3 p-6">
                <span className="h-3 w-24 animate-pulse rounded bg-surface-4" />
                <span className="h-9 w-32 animate-pulse rounded bg-surface-4" />
                <span className="h-3 w-full animate-pulse rounded bg-surface-4" />
              </div>
            ))}
          </div>
          <div className="space-y-2 rounded-xl border border-surface-border p-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center justify-between py-2">
                <span className="h-4 w-32 animate-pulse rounded bg-surface-4" />
                <span className="h-4 w-16 animate-pulse rounded bg-surface-4" />
              </div>
            ))}
          </div>
        </div>
      )}
    </PageContainer>
  );

  const txnFilterDef = TXN_FILTERS.find((f) => f.key === txnFilter) || TXN_FILTERS[0];
  const filteredTxns = data.transactions.filter(txnFilterDef.match);
  const shownTxns = showAllTxns ? filteredTxns : filteredTxns.slice(0, 6);
  const redeemable = user?.cash || 0;
  const bonusAmt = user?.bonus || 0;

  // --- Dimension-aware identity model (UIA-06) ----------------------------------
  // identity_status is the ID-check RESULT; a compliance_hold is a separate override that must
  // suppress any "redemptions unlocked" language even when the ID itself is approved. We only say
  // redemptions are unlocked when the eligibility response (verif.can_redeem) actually permits it.
  const idStatus = verif?.identity_status || (user?.kyc_status === "approved" ? "approved" : (user?.kyc_status || "not_started"));
  const complianceHold = !!verif?.compliance_hold;
  const idApproved = idStatus === "approved";
  const idPending = idStatus === "pending_review" || idStatus === "in_progress" || (verif == null && user?.kyc_status === "pending");
  const idExpired = idStatus === "expired" || !!verif?.identity_expired;
  const idRejected = idStatus === "rejected" || (verif == null && user?.kyc_status === "rejected");
  // Redemptions unlocked only when eligibility permits (verif present) — fall back to the legacy
  // approved flag only before verif loads, and never when a hold is active.
  const redeemUnlocked = !complianceHold && (verif ? verif.can_redeem : idApproved);

  return (
    <div className="min-h-screen bg-surface-1 text-white">
      <PageContainer clearBottomNav>

      {/* New payout domain — renders only when enabled (synthetic env); otherwise null. */}
      <PayoutDomainSection />

      {/* §7 one-shot staggered entrance wrapper — header, promo and balances enter in sequence.
          Owns its own section rhythm so PageContainer still spaces it from the activity grid below.
          Reduced motion: static, no stagger. (The right-rail cards keep their own entrance.) */}
      <motion.div
        className="space-y-6 lg:space-y-8"
        variants={reduceMotion ? undefined : SECTION_STAGGER}
        initial={reduceMotion ? false : "hidden"}
        animate="show"
      >

      {/* Header + actions (design 04 Wallet — WA-01) */}
      <motion.div variants={reduceMotion ? undefined : SECTION_ITEM} className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-1 text-[11px] font-extrabold uppercase tracking-[0.2em] text-pnr-green">Your balances</p>
          <h1 className="font-display text-4xl font-black tracking-tighter sm:text-5xl">Wallet</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button data-testid="header-get-coins" onClick={() => setDepOpen(true)}
            className="focus-ring rounded-full bg-[#00D632] px-6 font-bold text-[#0A0A0A] hover:bg-[#00B829]">
            <Icon name="plus" size={16} className="mr-1.5" /> Get Coins
          </Button>
          <Button data-testid="header-redeem" variant="outline" onClick={onRedeem}
            className="focus-ring rounded-full border-surface-border px-6 font-bold text-white hover:border-border-strong hover:bg-surface-4">
            <Icon name="upload" size={16} className="mr-1.5" /> Redeem prizes
          </Button>
        </div>
      </motion.div>

      {promos.length > 0 ? (
        <motion.div variants={reduceMotion ? undefined : SECTION_ITEM} className="space-y-3" data-testid="wallet-promo-banner">
          {promos.map((p) => (
            <div key={p.id} data-testid={`wallet-promo-${p.id}`}
              className="surface-premium flex items-center gap-3 rounded-2xl px-4 py-3">
              <Icon name="promotions" size={24} className="shrink-0 text-[#00D632]" />
              <div>
                <p className="font-display text-base font-black tracking-tight text-[#00D632]">{p.title}</p>
                <p className="text-sm text-meta tabular-nums">{p.description || `${p.match_pct}% deposit match${p.max_bonus ? ` up to $${p.max_bonus}` : ""}`}{p.min_deposit ? ` · min deposit $${p.min_deposit}` : ""}</p>
              </div>
            </div>
          ))}
        </motion.div>
      ) : null}

      {/* Three balance cards (design 04 — balances row) */}
      <motion.div variants={reduceMotion ? undefined : SECTION_ITEM} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* PCoins */}
        <div className="flex flex-col rounded-2xl border border-[#00D632]/40 bg-[#00D632]/5 p-6" data-testid="coins-balance-card">
          <div className="flex items-center gap-2">
            <Icon name="pnrcoin" size={20} className="text-pnr-coin" />
            <p className="text-xs uppercase tracking-[0.2em] text-[#00D632]">PCoins · play</p>
          </div>
          <p className="mt-2 font-display text-4xl font-black tabular-nums tracking-tighter text-[#00D632]" data-testid="wallet-coins">
            {streamerMode ? "•••" : (user?.coins || 0).toFixed(2)}
          </p>
          <p className="mt-1 text-xs text-meta">For fun play. Not redeemable.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="coins-get" onClick={() => setDepOpen(true)}
              className="focus-ring rounded-full border border-[#00D632]/40 bg-[#00D632]/10 px-3 py-1.5 text-xs font-bold text-[#00D632] transition-colors hover:bg-[#00D632]/20">+ Get coins</button>
            <button data-testid="coins-daily-drop" onClick={claimBonus} disabled={busy || !data.bonus?.can_claim}
              className="focus-ring rounded-full border border-surface-border bg-surface-5 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-surface-4 disabled:opacity-50">Free daily drop</button>
          </div>
        </div>

        {/* PCash */}
        <div className="surface-premium flex flex-col rounded-2xl p-6 text-white" data-testid="cash-balance-card">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Icon name="pnrcash" size={20} className="text-pnr-cash" />
              <p className="text-xs uppercase tracking-[0.2em] text-white/60">PCash · redeemable</p>
            </div>
            <button data-testid="pnrcash-info-button" onClick={() => setInfoOpen(true)} aria-label="About PCash"
              className="focus-ring rounded-full text-[#71717a] transition-colors hover:text-white"><Icon name="support" size={16} /></button>
          </div>
          <p className="mt-2 font-display text-4xl font-black tabular-nums tracking-tighter" data-testid="wallet-cash">
            {streamerMode ? "•••" : `$${redeemable.toFixed(2)}`}
          </p>
          <p className="mt-1 text-xs text-meta tabular-nums">
            <b className="text-[#00D632]">1 PCash = $1 USD</b>{redeemable >= limits.min ? ` · ${fmtMoney(redeemable, "cash", { mask: streamerMode })} available to redeem` : ""}
          </p>
          <p className="mt-auto pt-3 text-[11px] text-[#71717a]">
            Minimum redemption ${limits.min.toLocaleString()} · sent to your chosen method. See the{" "}
            <Link to="/prize-redemptions" data-testid="wallet-redemption-policy-link" className="focus-ring text-[#00D632] underline underline-offset-2 hover:text-white">Prize Redemption Policy</Link>{" "}and{" "}
            <Link to="/sweeps-rules" data-testid="wallet-playthrough-link" className="focus-ring text-[#00D632] underline underline-offset-2 hover:text-white">playthrough rules</Link>.
          </p>
        </div>

        {/* Bonus funds */}
        <div className="surface-premium flex flex-col rounded-2xl p-6" data-testid="bonus-balance-card">
          <div className="flex items-center gap-2">
            <Icon name="pnrcash" size={20} className="text-pnr-cash" />
            <p className="text-xs uppercase tracking-[0.2em] text-[#00D632]">Bonus funds</p>
          </div>
          <p className="mt-2 font-display text-4xl font-black tabular-nums tracking-tighter text-[#00D632]" data-testid="wallet-bonus">
            {streamerMode ? "•••" : bonusAmt > 0 ? `$${bonusAmt.toFixed(2)}` : "—"}
          </p>
          <p className="mt-1 text-xs text-meta">
            {bonusAmt <= 0
              ? "Wager to unlock as PCash."
              : data?.bonus_no_playthrough
              ? "Free play — place any bet with it and the full balance unlocks as withdrawable cash."
              : "Playable in the casino, not directly withdrawable. Wager it to unlock as cash."}
          </p>
          {data?.bonus_restrict === "live_casino" && bonusAmt > 0 && (
            <p className="mt-1 text-xs font-semibold text-[#00D632]" data-testid="bonus-restrict-note">Usable in the Live Casino only (Live Dealer Blackjack &amp; Baccarat).</p>
          )}
          {data?.bonus_required > 0 ? (
            <div className="mt-4" data-testid="playthrough-bar">
              <div className="mb-1 flex justify-between text-xs text-meta">
                <span>Playthrough to unlock as cash</span>
                <span className="tabular-nums text-[#00D632]" data-testid="playthrough-label">{fmtMoney(Math.min(data.bonus_progress, data.bonus_required), "cash", { mask: streamerMode })} / {fmtMoney(data.bonus_required, "cash", { mask: streamerMode })}</span>
              </div>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface-border">
                <div className="h-full rounded-full bg-surface-5 transition-all duration-500"
                  style={{ width: `${Math.min(100, (data.bonus_progress / data.bonus_required) * 100).toFixed(1)}%` }} />
              </div>
              <p className="mt-1.5 text-[11px] text-meta">Wager {fmtMoney(Math.max(0, data.bonus_required - data.bonus_progress), "cash", { mask: streamerMode })} more in the casino to convert your bonus to withdrawable cash.</p>
            </div>
          ) : bonusAmt > 0 ? (
            <p className="mt-auto pt-3 text-[11px] text-meta" data-testid="playthrough-none">
              {data?.bonus_no_playthrough
                ? "No playthrough required — place any bet with this bonus and the full balance unlocks as withdrawable cash."
                : "No playthrough required on this bonus — it unlocks as withdrawable cash as you play."}
            </p>
          ) : (
            <p className="mt-auto pt-3 text-[11px] text-[#71717a]" data-testid="playthrough-none">
              Claim a bonus or deposit offer and its playthrough terms will appear here.
            </p>
          )}
        </div>
      </motion.div>
      </motion.div>

      {/* Two-column: recent activity | reward / method / identity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* left: recent activity */}
        <div className="lg:col-span-7">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-xl font-black tracking-tight sm:text-2xl">Recent activity</h2>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex flex-wrap gap-1.5" data-testid="txn-filters">
                {TXN_FILTERS.map((f) => {
                  const active = txnFilter === f.key;
                  return (
                    <button key={f.key} data-testid={`txn-filter-${f.key}`} aria-pressed={active}
                      onClick={() => { setTxnFilter(f.key); setShowAllTxns(false); }}
                      className={`focus-ring rounded-full px-3 py-1 text-xs font-bold transition-colors ${active ? "bg-white text-surface-1" : "bg-surface-5 text-meta hover:text-white"}`}>
                      {f.label}
                    </button>
                  );
                })}
              </div>
              {filteredTxns.length > 6 && (
                <button data-testid="txn-full-history" onClick={() => setShowAllTxns((v) => !v)}
                  className="focus-ring text-xs font-bold text-[#00D632] hover:underline">
                  {showAllTxns ? "Show less" : "Full history →"}
                </button>
              )}
            </div>
          </div>
          {filteredTxns.length === 0 ? (
            <EmptyState icon="wallet" testid="txn-empty"
              message={data.transactions.length === 0 ? "No transactions yet." : `No ${txnFilterDef.label.toLowerCase()} yet.`} />
          ) : (
          <div className="surface-premium overflow-hidden rounded-2xl">
            {(
              shownTxns.map((t) => {
                const credit = ["deposit", "payout", "admin_credit", "bonus", "tip_received"].includes(t.type);
                const cur = txCurrency(t);
                const isWithdraw = t.type === "withdraw";
                return (
                <div key={t.id} data-testid={`txn-${t.id}`} className="flex items-start justify-between border-b border-surface-border px-5 py-3 last:border-0">
                  <div className="min-w-0">
                    <p className="font-medium">{TYPE_LABEL[t.type] || t.type}</p>
                    {t.note ? <p className="text-xs text-meta" data-testid={`txn-note-${t.id}`}>{t.note}</p> : null}
                    <p className="text-xs text-meta/70 tabular-nums">{new Date(t.created_at).toLocaleString()}</p>
                    {/* Redemption lifecycle + reference (UIA-05) */}
                    {isWithdraw && (
                      <div className="mt-1.5" data-testid={`txn-lifecycle-${t.id}`}>
                        {t.status === "failed" || t.status === "rejected" ? (
                          <p className="text-[11px] text-[#EF4444]" data-testid={`txn-failed-${t.id}`}>
                            {t.status === "failed" ? "Payout failed" : "Declined"}{t.fail_reason ? ` — ${t.fail_reason}` : ""}
                            {t.failed_at ? ` · ${new Date(t.failed_at).toLocaleDateString()}` : ""}
                          </p>
                        ) : (
                          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-[#71717a]">
                            {WD_LIFECYCLE.map((stage, i) => {
                              const reached =
                                stage.key === "requested" ? true
                                : stage.key === "approved" ? ["approved", "sent", "paid"].includes(t.status)
                                : stage.key === "sent" ? (["sent", "paid"].includes(t.status) || !!t.sent_at)
                                : stage.key === "paid" ? (t.status === "paid" || !!t.paid_at)
                                : false;
                              return (
                                <span key={stage.key} className="flex items-center gap-1.5">
                                  {i > 0 && <span className="text-[#3f3f46]">→</span>}
                                  <span className={reached ? "font-semibold text-[#a1a1aa]" : ""}>{stage.label}</span>
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {(t.payout_method || t.payout_detail) && (
                          <p className="mt-0.5 text-[11px] text-[#71717a]" data-testid={`txn-dest-${t.id}`}>
                            {t.payout_method || "Method"}{t.payout_detail ? ` · ${maskDestination(t.payout_detail)}` : ""}
                          </p>
                        )}
                        {t.payout_reference && (
                          <p className="mt-0.5 text-[11px] text-[#71717a]" data-testid={`txn-ref-${t.id}`}>Ref: {t.payout_reference}</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5 pl-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[t.status] || "bg-surface-5 text-[#a1a1aa]"}`}>{STATUS_LABEL[t.status] || t.status}</span>
                    <span className={`font-bold tabular-nums ${credit ? "text-[#00D632]" : "text-white"}`}>
                      {credit ? "+" : "−"}{fmtAmtNum(t.amount, cur, { mask: streamerMode })}
                      {cur === "coins" && <span className="ml-1 text-xs font-normal text-[#a1a1aa]">PCoins</span>}
                      {cur === "bonus" && <span className="ml-1 text-xs font-normal text-[#a1a1aa]">bonus</span>}
                    </span>
                  </div>
                </div>
                );
              })
            )}
          </div>
          )}
        </div>

        {/* right rail: daily reward, saved method, identity, vip, referral */}
        <aside className="space-y-6 lg:col-span-5">
          {data.bonus && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-[#00D632] bg-[#00D632]/5 p-5" data-testid="bonus-card">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#00D632]">
                  <Icon name="sparkles" size={24} className="text-[#0A0A0A]" />
                </div>
                <div>
                  <h2 className="font-display text-lg font-bold tracking-tight">Free daily reward</h2>
                  <p className="text-sm text-[#a1a1aa]">
                    Claim <b className="tabular-nums text-white">{dailyRewardLabel(data.bonus)}</b> every 24h.
                  </p>
                  <p className="mt-1 text-xs text-[#a1a1aa]">{dailyRewardTerms(data.bonus)}</p>
                </div>
              </div>
              <Button data-testid="claim-bonus-button" onClick={claimBonus}
                disabled={busy || !data.bonus.can_claim}
                className="mt-4 w-full rounded-full bg-[#00D632] py-6 font-bold text-[#0A0A0A] hover:bg-[#00B829] disabled:opacity-50">
                {data.bonus.can_claim ? "Claim reward" : `Next claim ${data.bonus.next_claim_at ? new Date(data.bonus.next_claim_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "soon"}`}
              </Button>
            </motion.div>
          )}

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="surface-premium rounded-2xl p-5" data-testid="saved-payout-card">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-xs uppercase tracking-[0.2em] text-meta">Saved redemption method</p>
                  {data.saved_payout_method && idApproved && (
                    <span className="rounded-full bg-[#00D632]/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#00D632]" data-testid="saved-payout-verified" title="Your identity is verified. We confirm payout-destination ownership at redemption.">Identity verified</span>
                  )}
                  {data.saved_payout_method && idApproved && complianceHold && (
                    <span className="rounded-full bg-[#eab308]/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#eab308]" data-testid="saved-payout-hold" title="Your account is under compliance review. Redemptions are paused until it's resolved.">Review hold</span>
                  )}
                </div>
                {data.saved_payout_method ? (
                  <p className="mt-1 truncate text-sm" data-testid="saved-payout-value">
                    <b className="text-white">{data.saved_payout_method}</b> <span className="text-meta">— {data.saved_payout_detail}</span>
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-meta">No default saved. Add one to skip retyping at redemption.</p>
                )}
              </div>
              {!pmEdit && (
                <div className="flex shrink-0 gap-2">
                  <Button size="sm" variant="outline" data-testid="edit-payout-button" onClick={startEditPm} className="focus-ring rounded-full border-surface-border text-white hover:border-border-strong hover:bg-surface-5">
                    {data.saved_payout_method ? "Edit" : "Add"}
                  </Button>
                  {data.saved_payout_method && (
                    <Button size="sm" variant="outline" data-testid="clear-payout-button" onClick={clearPm} className="focus-ring rounded-full border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10">Clear</Button>
                  )}
                </div>
              )}
            </div>
            {pmEdit && (
              <div className="mt-4 space-y-3" data-testid="edit-payout-form">
                <div>
                  <label htmlFor="pm-method-select" className="text-xs uppercase tracking-[0.2em] text-meta">Method</label>
                  <select id="pm-method-select" value={pmMethod} data-testid="payout-method-select" name="payout_method" onChange={(e) => setPmMethod(e.target.value)}
                    className="focus-ring mt-1.5 h-10 w-full rounded-md border border-surface-border bg-surface-5 px-3 text-sm text-white">
                    {["PayPal", "Venmo", "Bank transfer (ACH)", "Crypto (BTC)", "Crypto (USDT)"].map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="pm-detail-input" className="text-xs uppercase tracking-[0.2em] text-meta">
                    {pmMethod === "PayPal" ? "PayPal email" : pmMethod === "Venmo" ? "Venmo @username" : pmMethod.startsWith("Bank") ? "Account & routing" : pmMethod.startsWith("Crypto") ? `${pmMethod.includes("USDT") ? "USDT" : "BTC"} wallet address` : "Destination"}
                  </label>
                  <Input id="pm-detail-input" value={pmDetail} data-testid="payout-detail-input" onChange={(e) => setPmDetail(e.target.value)} placeholder="Your payout details" className="mt-1.5" />
                </div>
                {/* Payout-destination ownership verification. Name-match for PayPal/Venmo/bank;
                    crypto signed-message verification is coming soon. */}
                {pmMethod.startsWith("Crypto") ? (
                  <p className="rounded-lg bg-surface-5 px-3 py-2 text-xs text-meta" data-testid="crypto-verify-soon">
                    Ownership verification for crypto wallets (signed-message) is coming soon.
                  </p>
                ) : (
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-meta">Full name on the account</label>
                    <Input value={pmHolder} data-testid="payout-holder-input" onChange={(e) => setPmHolder(e.target.value)}
                      placeholder="Must match your verified identity" className="mt-1.5" />
                    <p className="mt-1 text-[11px] text-[#71717a]">Redemptions can only be sent to an account in your own name.</p>
                    <Button size="sm" data-testid="verify-payout-button" disabled={pmVerifying} onClick={verifyPm}
                      className="focus-ring mt-2 rounded-full border border-[#00D632]/40 bg-[#00D632]/10 font-bold text-[#00D632] hover:bg-[#00D632]/20">
                      {pmVerifying ? "Verifying…" : "Verify ownership"}
                    </Button>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button size="sm" data-testid="save-payout-button" onClick={savePm} className="focus-ring rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">Save default</Button>
                  <Button size="sm" variant="outline" onClick={() => setPmEdit(false)} className="focus-ring rounded-full border-surface-border text-white hover:border-border-strong hover:bg-surface-5">Cancel</Button>
                </div>
              </div>
            )}
          </motion.div>

          {/* Identity verification status (design 04 Wallet — "Identity verified").
              Dimension-aware (UIA-06): "Identity verified" reflects ONLY the ID result; a
              compliance hold is surfaced separately and overrides any "redemptions unlocked"
              language — we never say redemptions are open unless eligibility (redeemUnlocked)
              actually permits it. */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="surface-premium rounded-2xl p-5" data-testid="identity-card">
            {idApproved ? (
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#00D632]/15"><Icon name="check-circle" size={22} className="text-[#00D632]" /></span>
                <div>
                  <p className="font-display text-base font-bold tracking-tight text-white">Identity verified</p>
                  {complianceHold ? (
                    <p className="mt-1 text-sm text-[#eab308]" data-testid="identity-hold">
                      Your account is under compliance review{verif?.compliance_hold_reason ? `: ${verif.compliance_hold_reason}` : ""}. Redemptions are paused until it's resolved — contact Support if you have questions.
                    </p>
                  ) : (
                    <p className="mt-1 text-sm text-meta">Identity approved{user?.age_verified ? " · age 18+ confirmed" : ""}. {redeemUnlocked ? "Redemptions are processed within 24–48h." : "Complete any remaining checks to unlock redemptions."}</p>
                  )}
                </div>
              </div>
            ) : idPending ? (
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface-5"><Icon name="clock" size={22} className="text-[#a1a1aa]" /></span>
                <div>
                  <p className="font-display text-base font-bold tracking-tight text-white">Verification pending</p>
                  <p className="mt-1 text-sm text-meta">Your verification is under review. We'll unlock redemptions as soon as you're approved.</p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface-5"><Icon name="id-card" size={22} className="text-[#a1a1aa]" /></span>
                <div>
                  <p className="font-display text-base font-bold tracking-tight text-white">{idExpired ? "Re-verify your identity" : "Verify your identity"}</p>
                  <p className="mt-1 text-sm text-meta">{idRejected ? "Your last submission was declined — resubmit clear photos from Redeem prizes." : idExpired ? "Your verification has expired. Re-verify from Redeem prizes to keep redemptions open." : "A one-time ID check unlocks prize redemptions. Start it from Redeem prizes."}</p>
                  <button data-testid="identity-start" onClick={() => setWdOpen(true)} className="focus-ring mt-2 rounded text-xs font-bold text-[#00D632] hover:underline">{idExpired ? "Re-verify" : "Start verification"} →</button>
                </div>
              </div>
            )}
          </motion.div>

          {user?.vip && (
            <div className="rounded-2xl border border-[#00D632]/40 bg-[#00D632]/[0.06] p-5" data-testid="vip-card">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#00D632]/12">
                  <Icon name="vip" size={24} className="text-[#00D632]" />
                </div>
                <div>
                  <h2 className="font-display text-lg font-bold tracking-tight text-white">{user.vip.tier} tier · Level {user.vip.level}</h2>
                  <p className="text-sm text-meta tabular-nums">Daily reward: <b>{data.bonus?.amount != null ? `${Number(data.bonus.amount).toLocaleString()} PCoins` : "—"}</b> · Wagered: {user.vip.total_wagered.toLocaleString()}</p>
                </div>
              </div>
              {user.vip.next_tier && (
                <div className="mt-4">
                  <div className="mb-1 flex justify-between text-xs text-meta">
                    <span>Progress to {user.vip.next_tier}</span>
                    <span className="tabular-nums">{user.vip.progress}%</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-surface-border">
                    <div className="h-full rounded-full bg-[#00D632]" style={{ width: `${user.vip.progress}%` }} />
                  </div>
                </div>
              )}
            </div>
          )}

          {ref && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              className="surface-premium rounded-2xl p-5" data-testid="referral-card">
              <div className="flex items-center gap-2">
                <Icon name="promotions" size={16} className="text-[#00D632]" />
                <h2 className="font-display text-lg font-bold tracking-tight">Refer friends</h2>
              </div>
              <p className="mt-1 text-sm text-meta">Share your code — new users enter it when they sign up.</p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <div className="rounded-lg border border-dashed border-[#00D632] bg-[#00D632]/5 px-4 py-2">
                  <span className="font-display text-2xl font-black tracking-widest" data-testid="referral-code">{ref.referral_code}</span>
                </div>
                <Button variant="outline" size="sm" data-testid="copy-referral-button"
                  className="focus-ring"
                  onClick={() => { navigator.clipboard.writeText(ref.referral_code); toast.success("Referral code copied"); }}>
                  <Icon name="copy" size={12} className="mr-1" /> Copy
                </Button>
              </div>
              <p className="mt-2 text-sm text-meta">
                <span className="font-bold tabular-nums text-white" data-testid="referral-count">{ref.count}</span> signed up with your code
              </p>
              {ref.referred.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {ref.referred.map((r, i) => (
                    <span key={i} className="rounded-full bg-surface-5 px-3 py-1 text-xs font-medium">{r.username}</span>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </aside>
      </div>
      </PageContainer>

      {/* ============ Controlled dialogs (opened from header / cards) ============ */}

      {/* PCash info */}
      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-surface-border bg-surface-4 text-white shadow-[var(--elev-4)]" data-testid="pnrcash-info-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Information</DialogTitle></DialogHeader><DialogDescription className="sr-only">How PCash works and how it is redeemed.</DialogDescription>
          <div className="space-y-3">
            <p className="font-display text-lg font-black tracking-tight">PCash</p>
            <p className="text-sm text-meta">PCash can be redeemed for USD that is transferred to the user's bank account at the ratio of <b className="text-[#00D632]">1 PCash = $1 USD</b>.</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm"><span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#00D632]" /> Make sports picks to win more PCash</li>
              <li className="flex items-start gap-2 text-sm"><span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#00D632]" /> Purchase PRYMEBET coins with PCash</li>
            </ul>
          </div>
          <DialogFooter>
            <Button data-testid="pnrcash-info-gotit" onClick={() => setInfoOpen(false)} className="focus-ring w-full rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purchase (Stripe / Razorpay) */}
      <Dialog open={depOpen} onOpenChange={setDepOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-surface-border bg-surface-4 text-white shadow-[var(--elev-4)]">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Choose a coin pack</DialogTitle></DialogHeader><DialogDescription className="sr-only">Pick a coin pack to purchase PCoins plus bonus PCash.</DialogDescription>
          <div className="space-y-3">
            {firstBonus.available && (
              <div data-testid="first-purchase-banner" className="rounded-xl border border-[#00D632]/40 bg-[#00D632]/10 p-3 text-center">
                <p className="flex items-center justify-center gap-1.5 font-display text-sm font-black text-[#00D632]"><Icon name="promotions" size={14} /> First-purchase bonus!</p>
                <p className="text-xs text-white/80">Your very first pack gets <b>+{firstBonus.coinPct}% PCoins</b> and <b>+{firstBonus.cashPct}% PCash</b> — automatically.</p>
              </div>
            )}
            {promos.filter((p) => p.promo_type === "deposit_match").length > 0 && (
              <div className="space-y-2" data-testid="buy-promos">
                <p className="text-xs font-bold uppercase tracking-wider text-[#a1a1aa]">Choose your offer</p>
                {promos.filter((p) => p.promo_type === "deposit_match").map((p) => {
                  const sel = chosenPromo === p.id;
                  return (
                  <button key={p.id} type="button" data-testid={`buy-promo-${p.id}`} onClick={() => setChosenPromo(p.id)} aria-pressed={sel}
                    className={`focus-ring w-full rounded-xl border p-3 text-left transition-colors ${sel ? "border-[#00D632] shadow-[inset_0_0_0_1px_rgba(0,214,50,0.35)]" : "border-surface-border bg-white/5"}`}>
                    <div className="flex items-center gap-2">
                      {sel ? <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#00D632] text-[9px] font-black text-[#0A0A0A]">✓</span> : (p.restrict === "live_casino" ? <Icon name="sparkles" size={16} className="text-[#a1a1aa]" /> : <Icon name="promotions" size={16} className="text-[#00D632]" />)}
                      <p className={`font-display text-sm font-black ${p.restrict === "live_casino" ? "text-[#a1a1aa]" : "text-[#00D632]"}`}>{p.title || `${p.match_pct}% Deposit Match`}</p>
                      {p.restrict === "live_casino" && <span className="rounded bg-white/5 px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#a1a1aa]">Live Casino</span>}
                    </div>
                    <p className="mt-1 text-xs text-white/80">
                      {p.description || `Get a ${p.match_pct}% bonus${p.max_bonus ? ` up to $${p.max_bonus}` : ""}`}
                      {p.min_deposit ? ` · min $${p.min_deposit}` : ""}
                      {p.playthrough_mult != null ? ` · ${p.playthrough_mult}× playthrough` : ""}
                    </p>
                  </button>
                  );
                })}
                <button type="button" data-testid="buy-promo-none" onClick={() => setChosenPromo("")} aria-pressed={chosenPromo === ""}
                  className={`focus-ring w-full rounded-xl border p-2.5 text-left text-xs transition-colors ${chosenPromo === "" ? "border-[#00D632] text-white shadow-[inset_0_0_0_1px_rgba(0,214,50,0.35)]" : "border-surface-border text-meta"}`}>
                  No offer — just the coins
                </button>
              </div>
            )}
            {packages.map((p) => (
              <div key={p.lookup_key} data-testid={`pkg-${p.lookup_key}`} className="rounded-xl border border-surface-border bg-surface-5 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-display text-base font-black tracking-tight">{p.label}</p>
                    <p className="text-sm tabular-nums text-[#00D632]">{p.coins.toLocaleString()} PCoins <span className="text-meta">+ ${Number(p.cash).toFixed(2)} PCash</span></p>
                    {firstBonus.available && (
                      <p className="mt-0.5 text-xs font-semibold tabular-nums text-[#00D632]" data-testid={`pkg-bonus-${p.lookup_key}`}>
                        +{Math.round(p.coins * firstBonus.coinPct / 100).toLocaleString()} bonus coins &amp; +${(p.cash * firstBonus.cashPct / 100).toFixed(2)} bonus cash
                      </p>
                    )}
                  </div>
                  <span className="rounded-lg bg-[#00D632]/10 px-2.5 py-1 text-sm font-black tabular-nums text-[#00D632]">${p.usd.toFixed(2)}</span>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button data-testid={`buy-stripe-${p.lookup_key}`} onClick={() => buyStripe(p.lookup_key)} disabled={!!buying}
                    className="focus-ring flex-1 rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">
                    {buying === p.lookup_key ? <Icon name="refresh" size={16} className="animate-spin" /> : <><Icon name="credit-card" size={16} className="mr-1.5" /> Card / Pay</>}
                  </Button>
                  {methods.razorpay && (
                    <Button data-testid={`buy-razorpay-${p.lookup_key}`} onClick={() => buyRazorpay(p.lookup_key)} disabled={!!buying}
                      variant="outline" className="focus-ring flex-1 rounded-full border-surface-border font-bold text-white hover:border-border-strong hover:bg-surface-4">
                      UPI / Razorpay
                    </Button>
                  )}
                </div>
              </div>
            ))}
            <div className="rounded-xl border border-dashed border-[#00D632]/40 bg-surface-5 p-4" data-testid="pkg-custom">
              <div className="flex items-center justify-between">
                <div>
                  <label htmlFor="custom-amount-input" className="font-display text-base font-black tracking-tight">Custom amount</label>
                  <p className="text-xs tabular-nums text-meta">Choose your own top-up (${customCfg.min_usd}–${customCfg.max_usd}). Base rate: {customCfg.coins_per_usd.toLocaleString()} PCoins + ${customCfg.cash_per_usd} PCash per $1.</p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <div className="relative flex-1">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-meta">$</span>
                  <input id="custom-amount-input" data-testid="custom-amount-input" name="custom_usd" type="number" min={customCfg.min_usd} max={customCfg.max_usd} step="1"
                    value={customUsd} onChange={(e) => setCustomUsd(e.target.value)} placeholder={`${customCfg.min_usd}`}
                    className="w-full rounded-full border border-surface-border bg-surface-0 py-2.5 pl-7 pr-3 text-sm tabular-nums text-white outline-none focus:border-[#00D632]" />
                </div>
                <Button data-testid="buy-custom" onClick={buyCustom} disabled={!!buying}
                  className="focus-ring rounded-full bg-[#00D632] px-5 font-bold text-[#0A0A0A] hover:bg-[#00B829]">
                  {buying === "pnr_custom" ? <Icon name="refresh" size={16} className="animate-spin" /> : <><Icon name="credit-card" size={16} className="mr-1.5" /> Buy</>}
                </Button>
              </div>
              {parseFloat(customUsd) > 0 && (
                <p className="mt-2 text-xs tabular-nums text-[#00D632]" data-testid="custom-amount-preview">
                  You get {Math.round(parseFloat(customUsd) * customCfg.coins_per_usd).toLocaleString()} PCoins + ${(parseFloat(customUsd) * customCfg.cash_per_usd).toFixed(2)} PCash
                  {firstBonus.available && <span className="text-[#00D632]"> · +{Math.round(parseFloat(customUsd) * customCfg.coins_per_usd * firstBonus.coinPct / 100).toLocaleString()} bonus coins &amp; +${(parseFloat(customUsd) * customCfg.cash_per_usd * firstBonus.cashPct / 100).toFixed(2)} bonus cash</span>}
                </p>
              )}
            </div>
            <p className="text-xs text-meta">Secure checkout. PCoins are for play; bonus PCash may be redeemable per the Sweepstakes Rules. 18+. No purchase necessary.</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Withdraw / Prize Redemption */}
      <Dialog open={wdOpen} onOpenChange={(o) => { setWdOpen(o); if (!o) { setWdReview(false); setWdResult(null); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-surface-border bg-surface-4 text-white shadow-[var(--elev-4)]">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Prize Redemption</DialogTitle></DialogHeader><DialogDescription className="sr-only">Request a redemption of your PCash balance.</DialogDescription>
          {wdResult ? (
            <div className="space-y-4" data-testid="withdraw-result">
              <div className="flex items-start gap-3 rounded-lg border border-[#00D632]/30 bg-[#00D632]/5 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#00D632]/15"><Icon name="check-circle" size={22} className="text-[#00D632]" /></span>
                <div className="min-w-0">
                  <p className="font-display text-base font-bold tracking-tight text-white">Redemption requested</p>
                  <p className="mt-1 text-sm text-meta">An admin will review and process your prize. Track progress under Recent activity.</p>
                </div>
              </div>
              <dl className="space-y-2 rounded-lg border border-surface-border bg-surface-5 p-3 text-sm">
                <div className="flex justify-between gap-3"><dt className="text-meta">Amount</dt><dd className="font-bold tabular-nums text-white">{fmtMoney(wdResult.amount, "cash")}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-meta">Method</dt><dd className="text-white">{wdResult.method}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-meta">Destination</dt><dd className="truncate text-white">{maskDestination(wdResult.detail)}</dd></div>
                {wdResult.reference && (
                  <div className="flex justify-between gap-3"><dt className="text-meta">Reference</dt><dd className="font-mono text-xs tabular-nums text-white" data-testid="withdraw-result-ref">{wdResult.reference}</dd></div>
                )}
              </dl>
              <DialogFooter>
                <Button data-testid="withdraw-result-done" onClick={() => { setWdResult(null); setWdOpen(false); }}
                  className="focus-ring w-full rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">Done</Button>
              </DialogFooter>
            </div>
          ) : wdReview ? (
            <div className="space-y-4" data-testid="withdraw-review">
              <p className="rounded-lg border border-[#00D632]/30 bg-[#00D632]/5 px-3 py-2 text-xs text-meta">Review your redemption before submitting. This can't be edited once requested.</p>
              <dl className="space-y-2 rounded-lg border border-surface-border bg-surface-5 p-3 text-sm">
                <div className="flex justify-between gap-3"><dt className="text-meta">Amount</dt><dd className="font-bold tabular-nums text-white" data-testid="withdraw-review-amount">{fmtMoney(parseFloat(wdAmt) || 0, "cash")}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-meta">Method</dt><dd className="text-white" data-testid="withdraw-review-method">{wdMethod}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-meta">Destination</dt><dd className="truncate text-white" data-testid="withdraw-review-dest">{maskDestination(wdTag)}</dd></div>
              </dl>
              <DialogFooter className="flex gap-2 sm:justify-between">
                <Button variant="outline" data-testid="withdraw-review-back" onClick={() => setWdReview(false)}
                  className="focus-ring rounded-full border-surface-border text-white hover:border-border-strong hover:bg-surface-5">Back</Button>
                <Button data-testid="withdraw-review-confirm" onClick={submitWithdraw} disabled={busy}
                  className="focus-ring rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">
                  {busy ? "Submitting..." : "Confirm redemption"}
                </Button>
              </DialogFooter>
            </div>
          ) : (user && user.kyc_status === "approved") ? (
            <>
              <div className="space-y-4">
                <p className="rounded-lg border border-[#00D632]/30 bg-[#00D632]/5 px-3 py-2 text-xs text-meta" data-testid="redeem-ratio-note"><b className="text-[#00D632]">1 PCash = $1 USD</b> — sent to your chosen method.</p>
                <div>
                  <label htmlFor="wd-amount-input" className="text-xs uppercase tracking-[0.2em] text-meta">Amount ($)</label>
                  <Input id="wd-amount-input" type="number" min={limits.min} max={limits.max} step="0.01" value={wdAmt} data-testid="withdraw-amount-input"
                    onChange={(e) => setWdAmt(e.target.value)} placeholder="0.00" className="mt-1.5 tabular-nums" />
                  <p className="mt-1 text-[11px] tabular-nums text-meta" data-testid="redeem-limits-note">Minimum <b className="text-white">${limits.min.toLocaleString()}</b> · Maximum <b className="text-white">${limits.max.toLocaleString()}</b> per request · ${limits.monthly_cap.toLocaleString()}/month cap</p>
                </div>
                <div>
                  <label htmlFor="wd-method-select" className="text-xs uppercase tracking-[0.2em] text-meta">Redemption method</label>
                  <select id="wd-method-select" value={wdMethod} data-testid="withdraw-method-select" name="withdraw_method"
                    onChange={(e) => setWdMethod(e.target.value)}
                    className="focus-ring mt-1.5 h-10 w-full rounded-md border border-surface-border bg-surface-5 px-3 text-sm text-white">
                    {["PayPal", "Venmo", "Bank transfer (ACH)", "Crypto (BTC)", "Crypto (USDT)"].map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="wd-tag-input" className="text-xs uppercase tracking-[0.2em] text-meta">
                    {wdMethod === "PayPal" ? "PayPal email" : wdMethod === "Venmo" ? "Venmo @username" : wdMethod.startsWith("Bank") ? "Account & routing (or details)" : wdMethod.startsWith("Crypto") ? `${wdMethod.includes("USDT") ? "USDT" : "BTC"} wallet address` : "Destination"}
                  </label>
                  <Input id="wd-tag-input" value={wdTag} data-testid="withdraw-tag-input"
                    onChange={(e) => setWdTag(e.target.value)}
                    placeholder={wdMethod === "PayPal" ? "you@email.com" : wdMethod === "Venmo" ? "@yourhandle" : wdMethod.startsWith("Bank") ? "Acct / routing" : wdMethod.startsWith("Crypto") ? "wallet address" : "destination"}
                    className="mt-1.5" />
                </div>
                <label htmlFor="wd-save-default" className="flex items-center gap-2 text-xs text-meta" data-testid="withdraw-save-default">
                  <input id="wd-save-default" type="checkbox" checked={wdSaveDefault} onChange={(e) => setWdSaveDefault(e.target.checked)} className="focus-ring h-4 w-4 accent-[#00D632]" />
                  Save this as my default redemption method
                </label>
                {!(user && user.age_verified) && (
                  <div>
                    <label htmlFor="wd-dob-input" className="text-xs uppercase tracking-[0.2em] text-meta">Date of birth (18+ required)</label>
                    <Input id="wd-dob-input" type="date" value={wdDob} data-testid="withdraw-dob-input"
                      onChange={(e) => setWdDob(e.target.value)}
                      max={new Date(Date.now() - 18 * 31557600000).toISOString().split("T")[0]}
                      className="mt-1.5" />
                    <p className="mt-1 text-xs text-meta">We verify your age before every payout.</p>
                  </div>
                )}
                <p className="text-xs text-meta">Funds are held on request. An admin will process your prize payout and approve it.</p>
              </div>
              <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 bg-surface-4/95 px-6 py-4 backdrop-blur supports-[backdrop-filter]:bg-surface-4/80">
                <Button data-testid="submit-withdraw-button" onClick={submitWithdraw} disabled={busy}
                  className="focus-ring w-full rounded-full bg-[#00D632] font-bold text-[#0A0A0A] shadow-[var(--elev-4)] hover:bg-[#00B829]/90">
                  {busy ? "Submitting..." : "Request redemption"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="space-y-4" data-testid="kyc-section">
              <div className="rounded-lg border border-surface-border bg-surface-5 p-3 text-sm text-meta">
                <p className="font-semibold text-white">Verify your identity to redeem</p>
                <p className="mt-1 text-xs">For your security and to comply with regulations, we verify every player before their first payout. This is a one-time check.</p>
                <p className="mt-2 text-xs">
                  We use third-party identity verification providers. Your information is handled under our{" "}
                  <Link to="/privacy" data-testid="kyc-privacy-link" className="focus-ring text-[#00D632] underline underline-offset-2 hover:text-white">Privacy Policy</Link>; where a verification method involves covered biometric data, the{" "}
                  <Link to="/biometric-notice" data-testid="kyc-biometric-link" className="focus-ring text-[#00D632] underline underline-offset-2 hover:text-white">Biometric Notice</Link>{" "}applies before capture.
                </p>
              </div>
              {user && user.kyc_status === "pending" ? (
                <div className="rounded-lg border border-surface-border bg-surface-5 p-4 text-center text-sm text-[#00D632]" data-testid="kyc-pending">
                  Your documents are under review. We'll unlock redemptions as soon as you're approved.
                </div>
              ) : (
                <>
                  {user && user.kyc_status === "rejected" && (
                    <div className="rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/10 p-3 text-sm text-[#EF4444]" data-testid="kyc-rejected">
                      Your previous submission was declined{user.kyc_reject_reason ? `: ${user.kyc_reject_reason}` : "."} Please try again.
                    </div>
                  )}
                  {deepidvEnabled && !manualKyc ? (
                    <div className="space-y-3" data-testid="kyc-deepidv">
                      <Button data-testid="start-deepidv-button" onClick={startDeepidv} disabled={kycBusy}
                        className="focus-ring w-full rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">
                        {kycBusy ? "Starting…" : "Verify my identity instantly"}
                      </Button>
                      <p className="text-center text-xs text-meta">
                        Takes about a minute — scan your government ID and take a selfie. Redemptions unlock automatically once you're approved.
                      </p>
                      <button type="button" data-testid="kyc-manual-toggle" onClick={() => setManualKyc(true)}
                        className="focus-ring w-full rounded text-center text-xs text-[#71717a] underline underline-offset-2 hover:text-white">
                        Upload documents manually instead
                      </button>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-meta">Government ID (photo)</label>
                        <Input type="file" accept="image/*" data-testid="kyc-id-input"
                          onChange={(e) => setKycId(e.target.files?.[0] || null)} className="mt-1.5 text-white file:text-white" />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-meta">Selfie</label>
                        <Input type="file" accept="image/*" data-testid="kyc-selfie-input"
                          onChange={(e) => setKycSelfie(e.target.files?.[0] || null)} className="mt-1.5 text-white file:text-white" />
                        <p className="mt-1 text-xs text-meta">Hold your ID next to your face for faster approval.</p>
                      </div>
                      <Button data-testid="submit-kyc-button" onClick={submitKyc} disabled={kycBusy}
                        className="focus-ring w-full rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">
                        {kycBusy ? "Uploading..." : "Submit for verification"}
                      </Button>
                      {deepidvEnabled && (
                        <button type="button" onClick={() => setManualKyc(false)}
                          className="focus-ring w-full rounded text-center text-xs text-[#71717a] underline underline-offset-2 hover:text-white">
                          ← Back to instant verification
                        </button>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CheckoutSheet
        open={!!checkout}
        order={checkout?.order}
        promoId={checkout?.promoId}
        onClose={() => { setCheckout(null); load(); refresh(); }}
      />
    </div>
  );
}

